import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import BpkBreakpoint, {
  BREAKPOINTS,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import AccountIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/account';
import AirportsIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/airports';
import BaggageIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/baggage-generic';
import BusIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/bus';
import SelfServiceIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/self-service';
import BpkImage, {
  withLazyLoading,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import {
  BpkBox,
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkPrice, {
  SIZES,
  ALIGNS,
} from '@skyscanner-internal/backpack-web/bpk-component-price';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  ThemeCardProps,
  Badge,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ThemeCardProps';

import STYLES from './ThemeCard.module.scss';

const documentIfExists = typeof window !== 'undefined' ? document : null;
const LazyLoadedImage = withLazyLoading(BpkImage, documentIfExists);
const IMG_LOGO_WIDTH = 48;
const IMG_LOGO_HEIGHT = 24;
const IMG_WIDTH = 392;
const IMG_HEIGHT = 184;

const IMG_WIDTH_MOBILE = 312;
const IMG_HEIGHT_MOBILE = 184;
const Icons: { [key: string]: JSX.Element } = {
  account: <AccountIcon />,
  baggage: <BaggageIcon />,
  bus: <BusIcon />,
  airports: <AirportsIcon />,
  'self-service': <SelfServiceIcon />,
};
type ThemeCardImageProps = Pick<
  ThemeCardProps,
  | 'disableImageLazyLoad'
  | 'imageAltText'
  | 'imageSrc'
  | 'isMobileForSSR'
  | 'imageConfig'
>;

const ThemeCardImage = ({
  disableImageLazyLoad = false,
  imageAltText,
  imageConfig,
  imageSrc,
  isMobileForSSR,
}: ThemeCardImageProps) => {
  const imgSrc = isMobileForSSR
    ? imageSrc.replace('_WxH', `_${IMG_WIDTH_MOBILE}x${IMG_HEIGHT_MOBILE}`)
    : imageSrc.replace('_WxH', `_${IMG_WIDTH}x${IMG_HEIGHT}`);

  const aspectRatio = isMobileForSSR
    ? IMG_WIDTH_MOBILE / IMG_HEIGHT_MOBILE
    : IMG_WIDTH / IMG_HEIGHT;
  switch (imageConfig?.layout) {
    case 'center':
      return (
        // BpkVessel (not BpkBox/BpkFlex): `.themeCardImageCenter` is nested
        // under `.themeCardImageCenterWrapper` without `&`, and its `img`
        // rule is a descendant selector reaching into either a raw <img> or
        // BpkImage's own internal rendered children — both ancestors must
        // keep their className. See
        // backpack-api-gaps/WOM-6777-themecard-inventorycard.md.
        <BpkVessel as="div" className={STYLES.themeCardImageCenterWrapper}>
          <BpkVessel as="div" className={STYLES.themeCardImageCenter}>
            {disableImageLazyLoad ? (
              <img src={imageSrc} alt={imageAltText} aria-hidden />
            ) : (
              <LazyLoadedImage
                src={imageSrc}
                aspectRatio={imageConfig.aspectRatio}
                altText={imageAltText}
                aria-hidden
              />
            )}
          </BpkVessel>
        </BpkVessel>
      );
    case 'cover':
    default:
      return (
        // BpkVessel (not BpkBox/BpkFlex): needs `border-radius` (top corners
        // only — `border-radius` is typed `never` on BpkBox/BpkFlex) plus
        // descendant selectors into BpkImage's own internal `div`/`img`. See
        // backpack-api-gaps/WOM-6777-themecard-inventorycard.md.
        <BpkVessel as="div" className={STYLES.ThemeCard__themeCardImage}>
          {disableImageLazyLoad ? (
            <img src={imgSrc} alt={imageAltText} aria-hidden />
          ) : (
            <LazyLoadedImage
              aspectRatio={aspectRatio}
              altText={imageAltText}
              src={imgSrc}
              aria-hidden
            />
          )}
        </BpkVessel>
      );
  }
};
const Badges = ({ badges }: { badges: Badge[] }) => (
  // `marginRight` (not the logical `marginEnd`): the original `margin-right`
  // has no `@include utils.bpk-rtl` override, so it stays physically on the
  // right in RTL too — unlike `.badgeIcon`'s own `margin-inline-end`, which
  // was already logical.
  <BpkFlex
    wrap="wrap"
    gap={BpkSpacing.MD}
    marginTop={BpkSpacing.MD}
    marginRight={BpkSpacing.MD}
  >
    {badges.map((badge) => (
      <BpkBadge
        key={badge.title}
        type={BADGE_TYPES.normal}
        aria-label={badge.ariaLabel}
        role="status"
        data-cy="bpk-badge"
      >
        {badge?.icon && Icons[badge.icon] ? (
          <BpkFlex
            as="span"
            inline
            marginTop={BpkSpacing.SM}
            marginEnd={BpkSpacing.SM}
          >
            {Icons[badge.icon]}
          </BpkFlex>
        ) : null}
        {badge.title}
      </BpkBadge>
    ))}
  </BpkFlex>
);
const ThemeCard = ({
  badges = undefined,
  disableImageLazyLoad = false,
  imageAltText,
  imageConfig = undefined,
  imageSrc,
  index,
  isMobileForSSR,
  isNeverHasPrice,
  leadingText,
  logoName,
  logoUrl,
  noPriceString,
  onClick,
  price,
  redirectUrl,
  rel = undefined,
  showBrandLogo = false,
  showLargePrice = false,
  subtitle = undefined,
  target = undefined,
  textBody = undefined,
  title,
  topBadges = undefined,
  trailingText = undefined,
}: ThemeCardProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdName = `${rawComponentName}_item`;

  return (
    // BpkVessel (not BpkBox/BpkFlex): `.ThemeCard` (on the BpkCard below) and
    // `.themeCardImageCenterWrapper` are nested under `.ThemeCardWrapper`
    // without `&` in the SCSS, compiling to compound selectors
    // (`.ThemeCardWrapper .ThemeCard` etc.) that require this className to
    // remain in the DOM as an ancestor. `.ThemeCard` itself can't drop its
    // className anyway: `$card-margin: bpk-spacing-base() * 0.75` = 0.75rem,
    // and BpkMarginValue only accepts spacing tokens/percentages/auto, not
    // arbitrary rem (verified against tokens.js isValidMarginValue — no
    // token matches 0.75rem). See
    // backpack-api-gaps/WOM-6777-themecard-inventorycard.md.
    <BpkVessel
      as="div"
      {...(index != null && {
        'data-tracking-element-id': `${mbdName}_${index}`,
        'data-tracking-common-params': buildMBDParam(mbdName),
      })}
      className={STYLES.ThemeCardWrapper}
    >
      <BpkCard
        data-testid="theme-card"
        padded={false}
        className={STYLES.ThemeCard}
        href={redirectUrl}
        rel={rel}
        blank={target === '_blank'}
        onClick={onClick}
      >
        {topBadges && (
          // zIndex: without it, this sits at the same stack level (auto) as
          // the image's own positioned wrapper (used for aspect-ratio
          // placeholding). Being earlier in DOM order, it would then paint
          // *underneath* the image instead of on top of it.
          <BpkBox
            position="absolute"
            top={BpkSpacing.Base}
            right={BpkSpacing.Base}
            zIndex={1}
          >
            <Badges badges={topBadges} />
          </BpkBox>
        )}
        {disableImageLazyLoad ? (
          // Render a single ThemeCardImage without BpkBreakpoint to avoid SSR
          // hydration mismatches. The image size depends on isMobileForSSR and
          // won't switch on viewport change, but this is acceptable for deal
          // cards which use center layout (no size substitution) or cover layout
          // where CSS object-fit handles the difference.
          <ThemeCardImage
            disableImageLazyLoad
            imageSrc={imageSrc}
            imageAltText={imageAltText}
            imageConfig={imageConfig}
            isMobileForSSR={isMobileForSSR}
          />
        ) : (
          <>
            <BpkBreakpoint query={BREAKPOINTS.MOBILE} matchSSR={isMobileForSSR}>
              <ThemeCardImage
                imageSrc={imageSrc}
                imageAltText={imageAltText}
                imageConfig={imageConfig}
                isMobileForSSR={isMobileForSSR}
              />
            </BpkBreakpoint>
            <BpkBreakpoint
              query={BREAKPOINTS.ABOVE_MOBILE}
              matchSSR={!isMobileForSSR}
            >
              <ThemeCardImage
                imageSrc={imageSrc}
                imageAltText={imageAltText}
                imageConfig={imageConfig}
                isMobileForSSR={isMobileForSSR}
              />
            </BpkBreakpoint>
          </>
        )}
        {showBrandLogo && logoUrl && (
          // BpkVessel (not BpkBox/BpkFlex): needs `float`, `box-shadow` and
          // `border-radius` (none exposed as props — `boxShadow`/`borderRadius`
          // are typed `never`), an RTL `float` flip, pre-existing `!important`
          // overrides, and descendant selectors into BpkImage's own internal
          // `div`/`img`. See
          // backpack-api-gaps/WOM-6777-themecard-inventorycard.md.
          <BpkVessel as="div" className={STYLES.ThemeCard__brandLogo}>
            {disableImageLazyLoad ? (
              <img src={logoUrl} alt={logoName} aria-hidden />
            ) : (
              <LazyLoadedImage
                src={logoUrl}
                alt={logoName}
                aspectRatio={IMG_LOGO_WIDTH / IMG_LOGO_HEIGHT}
                aria-hidden
              />
            )}
          </BpkVessel>
        )}
        {/* BpkVessel (not BpkBox/BpkFlex): needs `border-top`, which
            BpkBox/BpkFlex don't expose (`border`/`borderTop` are typed
            `never` in commonProps.d.ts). See
            backpack-api-gaps/WOM-6777-themecard-inventorycard.md. */}
        <BpkVessel as="div" className={STYLES.ThemeCard__main}>
          {/* align: bottom-aligns the title/price row by default (to line up
              with a two-line title + subtitle); with no subtitle, a
              single-line title leaves an uneven gap above it, so both blocks
              top-align instead. `showLargePrice` has no distinct layout here
              — the original `--largePrice` SCSS modifier was byte-identical
              to the base rule (dead/duplicate CSS), so it isn't ported. */}
          <BpkFlex
            justify="space-between"
            align={subtitle ? 'flex-end' : 'flex-start'}
          >
            {/* BpkVessel (not BpkBox/BpkFlex): ThemeCard.module.scss carries a
                `h3 { @include breakpoints.bpk-breakpoint-mobile { ... } }`
                descendant-selector rule that truncates the title to an
                ellipsis on mobile only. BpkText.truncate/lineClamp are not
                responsive (verified against BpkText.d.ts), so this
                breakpoint-driven swap cannot be expressed via a Backpack
                prop. See backpack-api-gaps/WOM-6777-themecard-inventorycard.md. */}
            <BpkVessel as="div" className={STYLES.ThemeCard__leftAligned}>
              <BpkText tagName="h3" textStyle={TEXT_STYLES.heading4}>
                {title}
              </BpkText>
              {subtitle && (
                <BpkText
                  textStyle={TEXT_STYLES.caption}
                  className={STYLES.ThemeCard__subtitle}
                >
                  {subtitle}
                </BpkText>
              )}

              {badges && <Badges badges={badges} />}
            </BpkVessel>

            {!isNeverHasPrice && (
              // BpkVessel (not BpkBox/BpkFlex): needs `white-space: nowrap`,
              // which neither BpkBox nor BpkFlex expose as a prop. See
              // backpack-api-gaps/WOM-6777-themecard-inventorycard.md.
              <BpkVessel as="div" className={STYLES.ThemeCard__rightAligned}>
                {price ? (
                  <BpkPrice
                    data-testid="theme-card-price"
                    price={price}
                    size={showLargePrice ? SIZES.large : SIZES.small}
                    leadingText={leadingText}
                    align={ALIGNS.right}
                    trailingText={trailingText}
                  />
                ) : (
                  /* to make sure we occupy the same vertical space */ <BpkPrice
                    data-testid="theme-card-price"
                    price="&nbsp;"
                    size={SIZES.small}
                    leadingText={noPriceString}
                    align={ALIGNS.right}
                    trailingText="&nbsp;"
                  />
                )}
              </BpkVessel>
            )}
          </BpkFlex>
          {textBody && (
            <BpkText
              className={STYLES.ThemeCard__textBody}
              tagName="p"
              textStyle={TEXT_STYLES.bodyDefault}
            >
              {textBody}
            </BpkText>
          )}
        </BpkVessel>
      </BpkCard>
    </BpkVessel>
  );
};

export default ThemeCard;
